// O alinhamento da LEGENDA com a fala (spec-009 [US-2]), como regra pura — sem DOM, sem
// áudio, sem Vue. O player só passa o progresso e recebe o bloco que está sendo dito.
//
// ★ POR QUE PROPORCIONAL AO TAMANHO, e não por marca de tempo: o contrato de
// `POST /voz/sintetizar` devolve `{ audio_base64, content_type }` — e só. Não há
// timestamp por palavra nem por frase para alinhar, e inventar um seria fingir precisão
// que o dado não tem. O que existe de verdade é a duração total do áudio e o tamanho de
// cada bloco, e a fala humana consome tempo aproximadamente proporcional ao texto.
//
// A consequência, dita: o destaque acompanha a fala com a folga de alguns décimos de
// segundo, e um bloco com muitos números ou siglas (que se falam mais devagar) atrasa um
// pouco em relação ao destaque. É legenda de acompanhamento, não karaokê.
//
// ★ A PORTA PARA A PRECISÃO JÁ ESTÁ ABERTA: no dia em que a síntese devolver marcas de
// tempo, `pesosDosBlocos` passa a receber as durações reais em vez do comprimento, e
// `blocoNoProgresso` não muda uma linha — ele já trabalha sobre pesos relativos.

// Bloco vazio ainda ocupa um mínimo: sem isto, um bloco de 0 caracteres teria intervalo
// nulo e seria PULADO pelo destaque, sumindo da legenda.
const PESO_MINIMO = 1

/**
 * O peso de cada bloco na linha do tempo — hoje, o número de caracteres.
 * Devolve sempre um array do mesmo tamanho da entrada.
 */
export function pesosDosBlocos(blocos) {
  if (!Array.isArray(blocos)) return []
  return blocos.map((bloco) => Math.max(String(bloco || '').length, PESO_MINIMO))
}

/**
 * Qual bloco está sendo falado quando a reprodução está em `progresso` (0 a 1).
 *
 * Devolve `-1` quando não há o que destacar — sem blocos, ou progresso fora da faixa
 * (o que acontece antes de o áudio começar e quando a duração ainda é desconhecida:
 * `currentTime / NaN`). `-1` é "nenhum destaque", e NÃO o primeiro bloco: destacar o
 * bloco 1 antes de a fala começar mentiria sobre o que está sendo dito.
 */
export function blocoNoProgresso(pesos, progresso) {
  if (!Array.isArray(pesos) || pesos.length === 0) return -1
  if (typeof progresso !== 'number' || !isFinite(progresso) || progresso < 0) return -1

  const total = pesos.reduce((soma, peso) => soma + peso, 0)
  if (total <= 0) return -1
  // Progresso além do fim (o `timeupdate` do browser pode passar de 1 por arredondamento)
  // fica no último bloco, que é onde a fala de fato está.
  if (progresso >= 1) return pesos.length - 1

  const alvo = progresso * total
  let acumulado = 0
  for (let i = 0; i < pesos.length; i++) {
    acumulado += pesos[i]
    if (alvo < acumulado) return i
  }
  return pesos.length - 1
}

/**
 * O progresso (0 a 1) de um áudio, tolerante ao que o browser ainda não sabe.
 *
 * `duration` é `NaN` até os metadados carregarem e `Infinity` em stream sem tamanho —
 * nos dois casos a resposta é `-1` (nada a destacar), nunca uma divisão que produz `NaN`
 * silencioso mais adiante.
 */
export function progressoDoAudio(currentTime, duration) {
  const tempo = Number(currentTime)
  const total = Number(duration)
  if (!isFinite(tempo) || !isFinite(total) || total <= 0) return -1
  return Math.min(tempo / total, 1)
}
